import { Controller } from "@hotwired/stimulus"
import { get } from "@rails/request.js"

// Connects to data-controller="performances--attendance-field"
export default class extends Controller {
  static values = {
    date: String,
    attendanceIndex: Number
  }

  static targets = [
    "holidayCheckbox",
    "attendanceRule",
    "clockIn",
    "clockOut",
    "vacationPattern",
    "vacationTimeSlice",
    "vacationTime",
    "secondaryVacationPattern",
    "vacationPatternCell",
    "act",
    "over",
    "night",
    "hol",
    "vacation",
    "break",
  ]

  connect() {
    document.addEventListener("performances--attendance-field:activated", this.#handleOtherActivated)
    this.#restoreDisplayFromRecord()
  }

  disconnect() {
    document.removeEventListener("performances--attendance-field:activated", this.#handleOtherActivated)
  }

  calculateWorkTime(event) {
    if (!this.#hasAttendanceRule()) return

    const detail = event?.detail || {}

    get(this.#buildCalculateUrl(detail), { responseKind: "json" })
      .then(response => {
        if (!response.ok) {
          throw response
        }
        return response.json
      })
      .then(result => {
        this.#updateWorkTimeElements(result)
        this.dispatch("workTimeUpdated", { bubbles: true })
      }).catch(async (error) => {
        let message = "Failed to calculate work time."
        try {
          const data = await error.json
          message = data.error || message
        } catch (_) {
        }
        console.error(message)
      })
  }

  toggleActive() {
    this.dispatch("activated", { bubbles: true, target: document })
    this.element.classList.add("selected-row")
  }

  #handleOtherActivated = (event) => {
    if (event.target !== this.element) {
      this.element.classList.remove("selected-row")
    }
  }

  handleVacationPatternClick = () => {
    if (this.element.closest(`fieldset[disabled]`)) return
    if (this.holidayCheckboxTarget.checked) return
    if (!this.#hasAttendanceRule()) return
    this.#openVacationPatternModal()
  }

  markChangedByUser(event) {
    const field = event.currentTarget;
    const container = field.closest("tr");
    container.querySelector(`input[name$="[changed_by]"]`).value = "user";
  }

  #hasRequiredTimeInputs() {
    return this.clockInTarget.value && this.clockOutTarget.value && this.clockInTarget.value !== "--:--" && this.clockOutTarget.value !== "--:--"
  }

  #hasAttendanceRule() {
    if (this.attendanceRuleTarget.value) return true
    alert("Select an attendance rule first.")
    return false
  }


  #buildCalculateUrl(detail) {
    const url = new URL("/performances/calculate_work_time", window.location.origin)
    const params = {
      date: this.dateValue,
      attendance_rule_id: this.attendanceRuleTarget.value,
      clock_in: this.clockInTarget.value && this.clockInTarget.value !== "--:--" ? this.clockInTarget.value : undefined,
      clock_out: this.clockOutTarget.value && this.clockOutTarget.value !== "--:--" ? this.clockOutTarget.value : undefined,
      is_holiday: this.holidayCheckboxTarget.checked,
      vacation_pattern_id: this.vacationPatternTarget.value || undefined,
      secondary_vacation_pattern_id: this.secondaryVacationPatternTarget.value || undefined,
      vacation_time_slice_id: detail.vacationTimeSliceId || this.vacationTimeSliceTarget.value || undefined,
      vacation_time: detail.vacationTime || (this.hasVacationTimeTarget ? (this.vacationTimeTarget.value || undefined) : undefined),
    }
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined) url.searchParams.set(k, v)
    })
    return url
  }


  #updateWorkTimeElements({ total, overtime, night, holiday, vacation_time, break_time, vacation_days }) {
    this.#setWorkTime("totalWorkTime", this.actTarget, total)
    this.#setWorkTime("holidayWorkTime", this.holTarget, holiday)
    this.#setWorkTime("nightWorkTime", this.nightTarget, night)
    this.#setWorkTime("overtimeWorkTime", this.overTarget, overtime)
    this.#setWorkTime("breakTime", this.breakTarget, break_time)
    this.#setWorkTime("vacationTime", this.vacationTarget, vacation_time)

    this.element.dataset.vacationDays = vacation_days
  }

  #setWorkTime(dataKey, display, minutes) {
    this.element.dataset[dataKey] = minutes
    display.textContent = this.#formatTime(minutes)
  }

  #openVacationPatternModal() {
    const frame = document.getElementById("modal")
    if (!frame) return

    const form = this.element.closest("form")
    const url = new URL("/performances/vacation_pattern_modal", window.location.origin)

    const rawParams = {
      date: this.dateValue,
      attendance_rule_id: this.attendanceRuleTarget.value,
      attendance_index: this.attendanceIndexValue,
      worker_id: form.querySelector("[name='worker[id]']")?.value,
      vacation_pattern_id: this.secondaryVacationPatternTarget.value || this.vacationPatternTarget.value,
      vacation_time_slice_id: this.vacationTimeSliceTarget.value,
      vacation_time: this.vacationTimeTarget.value,
    }

    const params = Object.fromEntries(
      Object.entries(rawParams).filter(([_, v]) => v != null && v !== "")
    )

    Object.entries(params).forEach(([k, v]) => url.searchParams.append(k, v))

    if (frame.src === url.toString()) {
      frame.reload()
    } else {
      frame.src = url.toString()
    }
  }

  #restoreDisplayFromRecord() {
    const total = this.element.dataset.totalWorkTime
    const breakTime = this.element.dataset.breakTime
    const holiday = this.element.dataset.holidayWorkTime
    const night = this.element.dataset.nightWorkTime
    const overtime = this.element.dataset.overtimeWorkTime
    const vacationTime = this.element.dataset.vacationTime
    const vacationDays = this.element.dataset.vacationDays

    // if all empty this is a new record, nothing to restore
    if (!total && !holiday && !night && !overtime && !vacationTime && !vacationDays) return
    this.actTarget.textContent = this.#formatTime(total)
    this.holTarget.textContent = this.#formatTime(holiday)
    this.nightTarget.textContent = this.#formatTime(night)
    this.overTarget.textContent = this.#formatTime(overtime)
    this.breakTarget.textContent = this.#formatTime(breakTime)
    this.vacationTarget.textContent = this.#formatTime(vacationTime)
  }

  #formatTime(min) {
    if (!min || min <= 0) return "0時間0分"
    const h = Math.floor(min / 60), m = min % 60
    return `${h}時間${m}分`
  }
}
